// ---------------------------------------------------------------------------
// Repositorios (#1062): participación por repo y comparativos contra el mismo
// tamaño de rango movido hacia atrás. Lógica pura (se prueba sin montar nada).
//
// Regla de las ventanas: se conserva EXACTAMENTE el número de días del rango
// actual y se mueve su FIN hacia atrás —7 días (semana pasada) o 1, 6 y 12
// meses de calendario—. Si el día no existe en el mes destino (31 → 30 de
// abril) se usa el último día de ese mes. Así «quincena actual» (16–28 sep)
// se compara con 16–28 ago, 16–28 mar y 16–28 sep del año anterior.
//
// El backend recorta cualquier rango a `MAX_RANGE_DAYS` (366) días por el
// extremo antiguo y lo avisa con `rangeClamped`; como la ventana comparada
// mide lo mismo que la actual, nunca se recorta más que la actual.
// ---------------------------------------------------------------------------
import { addDays } from "@torre/work-hours";
import { rangeDays } from "./heatmap";

export const COMPARE_WINDOWS = ["week", "month", "sixMonths", "year"] as const;
export type CompareWindow = (typeof COMPARE_WINDOWS)[number];

export interface DateRange {
  from: string;
  to: string;
}

/** `YYYY-MM-DD` movido `months` meses de calendario (negativo = hacia atrás). */
export function shiftMonths(date: string, months: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const index = y * 12 + (m - 1) + months;
  const year = Math.floor(index / 12);
  const month = index - year * 12; // 0..11
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  return `${String(year).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Ventana comparable: mismos días que `range`, con el fin movido hacia atrás. */
export function comparisonRange(range: DateRange, window: CompareWindow): DateRange {
  const days = rangeDays(range.from, range.to);
  const to =
    window === "week"
      ? addDays(range.to, -7)
      : shiftMonths(range.to, window === "month" ? -1 : window === "sixMonths" ? -6 : -12);
  return { from: addDays(to, -(days - 1)), to };
}

export interface RepoHours {
  repo: string;
  hours: number;
}

export interface RepoShare extends RepoHours {
  /** Porcentaje del total del rango (0–100, dos decimales). */
  percent: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** % de las horas del rango por repo (sobre la suma de `byRepo`). */
export function repoShares(byRepo: RepoHours[]): RepoShare[] {
  const total = byRepo.reduce((n, r) => n + (Number(r.hours) || 0), 0);
  return byRepo.map((r) => ({ ...r, percent: total > 0 ? round2((r.hours / total) * 100) : 0 }));
}

/**
 * Los `top` repos con más horas y el resto agrupado en una sola fila
 * (`otherLabel`), para que la gráfica no se vuelva ilegible con muchos repos.
 */
export function topWithOthers(rows: RepoShare[], top: number, otherLabel: string): RepoShare[] {
  const sorted = [...rows].sort((a, b) => b.hours - a.hours || a.repo.localeCompare(b.repo));
  if (sorted.length <= top) return sorted;
  const rest = sorted.slice(top);
  return [
    ...sorted.slice(0, top),
    {
      repo: otherLabel,
      hours: round2(rest.reduce((n, r) => n + r.hours, 0)),
      percent: round2(rest.reduce((n, r) => n + r.percent, 0)),
    },
  ];
}

export interface RepoDelta {
  /** Horas de la ventana comparada (0 si el repo no tuvo horas ahí). */
  previous: number;
  /** Diferencia actual − comparada, en horas. */
  deltaHours: number;
  /** Variación en %; `null` si la ventana comparada no tuvo horas (no se divide entre 0). */
  deltaPercent: number | null;
}

/** Variación de un repo contra la ventana comparada. */
export function repoDelta(current: number, previousByRepo: Map<string, number>, repo: string): RepoDelta {
  const previous = previousByRepo.get(repo) ?? 0;
  return {
    previous,
    deltaHours: round2(current - previous),
    deltaPercent: previous > 0 ? round2(((current - previous) / previous) * 100) : null,
  };
}
